import type { LandingPageForm } from "@/features/landing/use-landing-page-form";
import type { AboutLoanForm } from "@/features/purpose/use-about-loan-form";
import type { EconomyForm } from "@/hooks/form/use-economy-form";
import type { PersonalInformationForm } from "@/hooks/form/use-personal-information-form";
import type { CachedLoanApplicationResponseBody } from "@/types/cached-loan-application";
import {
	ApplicationPurpose,
	DwellingType,
	EmploymentType,
	ExpenseType,
	IncomeType,
	MaritalStatus,
	Period,
} from "@/types/form";
import type { DeepUndefined } from "@/types/util-types";
import { splitMobileForForm } from "@/utils/mapping/prepare-cache-forms-for-api";
import type { NationalIdCountry } from "@/utils/validation/normalize-national-id";

interface DataByFrom {
	landingPageFormData: DeepUndefined<LandingPageForm>;
	aboutFormData: DeepUndefined<AboutLoanForm>;
	personalInformationFormData: DeepUndefined<PersonalInformationForm>;
	economyFormData: DeepUndefined<EconomyForm>;
}

const resolveHasCoApplicant = (
	cachedData: CachedLoanApplicationResponseBody
): boolean | undefined => {
	const stored = cachedData.params?.hasCoApplicant;
	if (stored === "true") return true;
	if (stored === "false") return false;

	const { coApplicant } = cachedData;
	const hasStoredCoApplicant = Boolean(
		coApplicant?.nationalId ||
		coApplicant?.mobileNumber ||
		coApplicant?.emailAddress
	);

	return hasStoredCoApplicant ? true : undefined;
};

/** `country` is the validation country of the market (`validationCountry`). */
export const cachedLoanApplicationToForms = (
	cachedData: CachedLoanApplicationResponseBody,
	country: NationalIdCountry
): DataByFrom => {
	const { applicant } = cachedData;
	const hasCoApplicant = resolveHasCoApplicant(cachedData);

	const coApplicant = hasCoApplicant ? cachedData.coApplicant : null;

	const storedRefinanceAmount = cachedData?.refinanceAmount ?? undefined;

	const aboutFormData: DeepUndefined<AboutLoanForm> = {
		isAffirmedRefinanceLoan:
			storedRefinanceAmount === undefined
				? undefined
				: storedRefinanceAmount > 0,
		appliedAmount: cachedData?.appliedAmount ?? undefined,
		appliedRepaymentTime: cachedData?.repaymentPeriod ?? undefined,
		purpose:
			cachedData?.purpose && cachedData?.purpose !== ""
				? (cachedData.purpose as ApplicationPurpose)
				: undefined,
		refinanceAmount: storedRefinanceAmount,
	};

	const applicantMobile = splitMobileForForm(
		applicant?.mobileNumber,
		country
	);
	const coApplicantMobile = splitMobileForForm(
		coApplicant?.mobileNumber,
		country
	);

	const personalInformationFormData: DeepUndefined<PersonalInformationForm> =
		{
			applicant: {
				nationalId: applicant?.nationalId ?? undefined,
				firstName: applicant?.firstName ?? undefined,
				lastName: applicant?.lastName ?? undefined,
				maritalStatus: applicant?.maritalStatus
					? (applicant?.maritalStatus as MaritalStatus)
					: undefined,
				mobileCountry: applicantMobile.mobileCountry,
				mobileNumber: applicantMobile.mobileNumber || undefined,
				emailAddress: applicant?.emailAddress ?? undefined,
				numberOfChildrenBelow18:
					applicant?.numberOfChildrenBelow18 ?? undefined,
			},
		};

	const economyFormData: DeepUndefined<EconomyForm> = {
		applicant: {
			economy: {
				dwelling: {
					type: applicant?.housing?.dwelling
						? (applicant?.housing.dwelling as DwellingType)
						: undefined,
					expenses: {
						amount:
							applicant?.housing?.monthlyCostRentOrMortgage ??
							undefined,
						type: ExpenseType.HousingExpenses,
						period: Period.Monthly,
					},
				},
				employment: {
					type: applicant?.employment?.type
						? (applicant?.employment.type as EmploymentType)
						: undefined,
					employer: applicant?.employment?.companyName ?? undefined,
					startDate: applicant?.employment?.startDate
						? new Date(applicant.employment.startDate)
						: undefined,
					numberOfCars:
						applicant?.employment?.numberOfCars ?? undefined,
				},
				income: {
					amount: applicant?.income?.monthlyNetPayout ?? undefined,
					type: IncomeType.GrossIncome,
					period: Period.Monthly,
				},
				debts: {
					mortgageLoan: applicant?.debts?.mortgageLoan ?? undefined,
					vehicleLoan: applicant?.debts?.vehicleLoan ?? undefined,
					studentLoan: applicant?.debts?.studentLoan ?? undefined,
					consumerLoan: applicant?.debts?.consumerLoan ?? undefined,
				},
			},
		},
		coApplicant: {
			applicant: {
				nationalId: coApplicant?.nationalId ?? undefined,
				firstName: coApplicant?.firstName ?? undefined,
				lastName: coApplicant?.lastName ?? undefined,
				mobileCountry: coApplicantMobile.mobileCountry,
				mobileNumber: coApplicantMobile.mobileNumber || undefined,
				emailAddress: coApplicant?.emailAddress ?? undefined,
				maritalStatus: coApplicant?.maritalStatus
					? (coApplicant.maritalStatus as MaritalStatus)
					: undefined,
				numberOfChildrenBelow18:
					coApplicant?.numberOfChildrenBelow18 ?? undefined,
			},
			economy: {
				dwelling: {
					type: coApplicant?.housing?.dwelling
						? (coApplicant.housing.dwelling as DwellingType)
						: undefined,
					expenses: {
						amount:
							coApplicant?.housing?.monthlyCostRentOrMortgage ??
							undefined,
						type: ExpenseType.HousingExpenses,
						period: Period.Monthly,
					},
				},
				employment: {
					type: coApplicant?.employment?.type
						? (coApplicant.employment.type as EmploymentType)
						: undefined,
					employer: coApplicant?.employment?.companyName ?? undefined,
					startDate: coApplicant?.employment?.startDate
						? new Date(coApplicant.employment.startDate)
						: undefined,
					numberOfCars:
						coApplicant?.employment?.numberOfCars ?? undefined,
				},
				income: {
					amount: coApplicant?.income?.monthlyNetPayout ?? undefined,
					type: IncomeType.GrossIncome,
					period: Period.Monthly,
				},
				debts: {
					mortgageLoan: coApplicant?.debts?.mortgageLoan ?? undefined,
					vehicleLoan: coApplicant?.debts?.vehicleLoan ?? undefined,
					studentLoan: coApplicant?.debts?.studentLoan ?? undefined,
					consumerLoan: coApplicant?.debts?.consumerLoan ?? undefined,
				},
			},
		},
		hasCoApplicant,
	};
	const consent = applicant?.consent;
	const thirdPartyShareConsent =
		consent?.thirdPartyShareConsent ?? consent?.thirdPartyShareConsen;

	const landingPageFormData: DeepUndefined<LandingPageForm> = {
		isAffirmedIntegrityPolicyCheckbox: consent
			? thirdPartyShareConsent === "true" &&
				consent.dataUsageConsent === "true"
			: undefined,
		isAffirmedMarketingCommunicationCheckbox: consent
			? consent.marketingConsent === "true"
			: undefined,
		campaignId: cachedData.campaignId ?? undefined,
		utmCampaign: cachedData.utmCampaign ?? undefined,
		utmSource: cachedData.utmSource ?? undefined,
		utmMedium: cachedData.utmMedium ?? undefined,
		utmTerm: cachedData.utmTerm ?? undefined,
		utmContent: cachedData.utmContent ?? undefined,
		adtractionId: cachedData.utmAdtraction ?? undefined,
		googleClickId: cachedData.googleClickId ?? undefined,
		facebookClickId: cachedData.facebookClickId ?? undefined,
	};

	return {
		aboutFormData,
		personalInformationFormData,
		economyFormData,
		landingPageFormData,
	} satisfies DataByFrom;
};
